import { Box, Flex, HStack, SimpleGrid, Text } from "@chakra-ui/react";
import { ArrowDown, ArrowUp } from "@phosphor-icons/react";
import { getFormattedNumValue } from "../../../../../utils";
import LocalSpinner from "../../../../../components/LocalSpinner";

const KpiCard = ({ accent, title, subtitle, value, previousValue, loading, positiveWhenUp = false }) => {
  const hasCurrent = typeof value === "number";
  const hasPrev = typeof previousValue === "number" && previousValue !== 0;
  const pctChange = hasCurrent && hasPrev ? ((value - previousValue) / previousValue) * 100 : null;
  // For engagement counts (suppliers, verified, finalized) an increase is good.
  const isImprovement = pctChange == null ? null : positiveWhenUp ? pctChange > 0 : pctChange < 0;
  const pillBg = isImprovement === null ? "gray.100" : isImprovement ? "green.50" : "red.50";
  const pillColor = isImprovement === null ? "gray.500" : isImprovement ? "green.700" : "red.600";
  const Arrow = pctChange == null ? null : pctChange < 0 ? ArrowDown : ArrowUp;

  return (
    <Box position="relative" pt={3} sx={{ "--big": "clamp(20px, 3vw, 56px)" }}>
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={accent} borderRadius="full" />
      <HStack align="baseline" spacing={2} mb={2}>
        <Text fontSize="15px" fontWeight="700" color="darkTeal">{title}</Text>
        {subtitle && <Text fontSize="13px" color="gray.500">{subtitle}</Text>}
      </HStack>
      {loading ? (
        <Text fontSize="var(--big)" fontWeight="700" color="darkTeal" lineHeight="1">…</Text>
      ) : (
        <HStack align="stretch" spacing={3} flexWrap="nowrap">
          <Text
            fontSize="var(--big)"
            fontWeight="700"
            color="darkTeal"
            lineHeight="1"
            letterSpacing="-0.02em"
            whiteSpace="nowrap"
          >
            {hasCurrent ? getFormattedNumValue(value, 0) : "—"}
          </Text>
          <Flex direction="column" justify="space-between" py={0.5} flexShrink={0}>
            {pctChange != null ? (
              <HStack
                spacing={1}
                bg={pillBg}
                color={pillColor}
                px={2}
                py={0.5}
                borderRadius="full"
                alignSelf="flex-start"
              >
                {Arrow && <Arrow size={11} weight="bold" />}
                <Text fontSize="11px" fontWeight="600">
                  {Math.abs(pctChange).toFixed(1)} %
                </Text>
              </HStack>
            ) : (
              <Box />
            )}
          </Flex>
        </HStack>
      )}
    </Box>
  );
};

const EngagementCards = ({ currentYearData, previousYearData, isLoading }) => {
  const inProgress =
    (currentYearData?.supplierCountWithRequestedData || 0) -
    (currentYearData?.totalSuppliersWithAllFinalizedData || 0);
  const prevInProgress =
    (previousYearData?.supplierCountWithRequestedData || 0) -
    (previousYearData?.totalSuppliersWithAllFinalizedData || 0);

  return (
    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={8}>
      <KpiCard
        accent="pureBlack"
        title="Suppliers"
        subtitle="engaged"
        value={currentYearData?.supplierCountWithRequestedData}
        previousValue={previousYearData?.supplierCountWithRequestedData}
        loading={isLoading}
        positiveWhenUp
      />
      <KpiCard
        accent="lime"
        title="Verified"
        subtitle="data verified"
        value={currentYearData?.verifiedSupplierCount}
        previousValue={previousYearData?.verifiedSupplierCount}
        loading={isLoading}
        positiveWhenUp
      />
      <KpiCard
        accent="lime"
        title="Finalised"
        subtitle="all data submitted"
        value={currentYearData?.totalSuppliersWithAllFinalizedData}
        previousValue={previousYearData?.totalSuppliersWithAllFinalizedData}
        loading={isLoading}
        positiveWhenUp
      />
      <KpiCard
        accent="red.400"
        title="In Progress"
        subtitle="not yet finalised"
        value={inProgress}
        previousValue={prevInProgress}
        loading={isLoading}
      />
    </SimpleGrid>
  );
};

export default EngagementCards;
